import { useState, useEffect, useRef } from 'react';
import { useToast } from '../../context/ToastProvider.js';
import config from '../../constants/config.js';
import './../../styles/Modal.css';

const statuses = [ 'Enabled', 'Disabled' ];

function GemstoneEditorModal({ 
    isOpen, 
    onClose, 
    onAdd, 
    initialData 
}) {
    const addToast = useToast();

    const [ gemstoneName, setGemstoneName ] = useState( '' );
    const [ gemstonePrice, setGemstonePrice ] = useState( 0.0 );
    const [ gemstoneId , setGemstoneId ] = useState( -1 );
    const [ status, setStatus ] = useState( statuses[0] );
    const [ files, setFiles ] = useState([]);
    const [ uploading , setUploading ] = useState( false );
    const noMutation = useRef( null );

    useEffect(() => {
        if ( initialData != null ) {
            setGemstoneName( initialData.gemstone_name || '');
            setStatus( initialData?.status || statuses[0] );

            if ( initialData?.gemstone_image_url != '' ) {
                initialData.files = [];
                initialData.files.push( { name: initialData.gemstone_name , id: initialData.id } );
            }

            setFiles( initialData?.files || [] );
            setGemstoneId( initialData?.id || -1 )
            setGemstonePrice( initialData?.gemstone_price || 0.0 );

            noMutation.current = initialData;
        } else {
            setGemstoneName( '' );
            setStatus( statuses[0] );
            setFiles( [] );
            setGemstoneId( -1 );
            setGemstonePrice( 0.0 );
        }
    }, [ initialData ] );

    function handleFileChange( e ) {
        const selectedFiles = Array.from(e.target.files);
        setFiles(prevFiles => {
            const newFiles = [...prevFiles];

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                let isDuplicate = false;

                for ( let j = 0; j < newFiles.length; j++ ) {
                    if ( newFiles[j].name === file.name ) {
                        isDuplicate = true;
                        break;
                    }
                }

                if ( !isDuplicate ) {
                    newFiles.push( file );
                }
            }

            return newFiles;
        });

        e.target.value = null;
    }

    function removeFile( indexToRemove ) {
        setFiles( prevFiles => {
            const newFiles = [];
            for (let i = 0; i < prevFiles.length; i++) {
                if ( i !== indexToRemove ) {
                    newFiles.push(prevFiles[i]);
                }
            }

            return newFiles;
        });
    }

    function validateFormForErrors() {
        const newErrors = {};

        if ( !gemstoneName.trim() ) { 
            newErrors.gemstoneName = 'Gemstone name is required';
        }

        if ( gemstonePrice < 0 ) { 
            newErrors.gemstonePrice = 'Gemstone price is required';
        }

        if ( !status ) { 
            newErrors.status = 'Status is required';
        }
       
        return newErrors;
    }

    async function handleSubmit() {
        setUploading( true );
        const formErrors = validateFormForErrors();
        const errorValues = Object.values( formErrors );
        const hasErrors = errorValues.length > 0;

        if ( hasErrors ) {
            console.log( formErrors );
            addToast( `Failed to save gemstone. ${errorValues[0]}`, true , 5000 );
            setUploading( false );
            return;
        }

        const payload = {
            gemstone_name: gemstoneName,
            gemstone_price: gemstonePrice,
            status: status,
            files: files,
        };

        if ( initialData != null ) {
            await updateGemstone( gemstoneName , status , gemstonePrice , files , gemstoneId );

            if ( typeof ( onAdd ) === 'function' ) {
                onAdd();
            }
        } else {
            await uploadGemstone( payload );

            if ( typeof ( onAdd ) === 'function' ) {
                onAdd();
            }
        }

        onClose();
        setUploading( false );
    }

    async function uploadGemstone( payload ) {
        try {
            // convert files array into binary and send to endpoint
            const filesAsBlobs = [];
            for ( let i = 0; i < payload?.files?.length; i++ ) {
                const file = payload.files[i];
                const buffer = await file.arrayBuffer();
                filesAsBlobs.push({
                    name: file.name,
                    type: file.type,
                    data: Array.from( new Uint8Array( buffer ) )
                });
            }

            payload.files = filesAsBlobs;

            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/gemstones`;
            const requestSettings = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify( payload )
            };

            const response = await fetch( url , requestSettings );
            const data = await response.json();
            if ( data?.errors?.length > 0 ) {
                addToast( `Failed to add gemstone - ${data?.errors[0].message}`, true , 5000 );
            } else {
                addToast( `Added Gemstone "${gemstoneName}"`, false , 5000 );
            }
        } catch ( error ) {
            console.log( error );
            addToast( `Failed to add gemstone - Could not reach server`, true , 5000 );
        }

        return;
    }

    async function updateGemstone( gemstoneName , status , gemstonePrice , files , gemstoneId ) {
        try {
            const newFiles = [];
            const removedFiles = [];
            const previousFiles = noMutation.current.files || [];
            for ( let i = 0; i < files?.length; i++ ) {
                const file = files[i];
                if ( typeof( file?.id ) === 'undefined' && file?.size > 0 ) {
                    const buffer = await file.arrayBuffer();
                    newFiles.push({
                        name: file.name,
                        type: file.type,
                        data: Array.from( new Uint8Array( buffer ) )
                    });
                }
            }

            for ( let i = 0; i < previousFiles.length; i++ ) {
                const prevFile = previousFiles[i];

                let stillExists = false;
                for ( let j = 0; j < files?.length; j++ ) {
                    const currFile = files[j];
                    if ( currFile?.id === prevFile.id ) {
                        stillExists = true;
                        break;
                    }
                }

                if ( !stillExists ) {
                    removedFiles.push( prevFile );
                }
            }

            const payload = {
                gemstone_name: gemstoneName,
                gemstone_price: gemstonePrice,
                status: status,
                new_files: newFiles,
                remove_files: removedFiles
            };

            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/gemstones/${encodeURIComponent( gemstoneId )}`;
            const requestSettings = {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify( payload )
            };

            const response = await fetch( url , requestSettings );
            const data = await response.json();
            if ( data?.errors?.length > 0 ) {
                addToast( `Failed to update gemstone - ${data?.errors[0].message}`, true , 5000 );
            } else {
                addToast( `Updated Gemstone "${gemstoneId}"`, false , 5000 );
            }
        } catch ( error ) {
            console.log( error );
        }


        return;
    }

    return(
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>
                    <span>×</span>
                    <span className="sr-only">Close modal</span>
                </button>

                <h1>{ initialData != null ? 'Edit Gemstone' : 'Add New Gemstone' }</h1>

                <div className="form-group">
                    <label>Gemstone Name<span className="required-asterisk">*</span></label>
                    <input type="text" value={gemstoneName} onChange={ e => setGemstoneName(e.target.value) } />
                </div>

                <div className="form-group">
                    <label>Gemstone Price<span className="required-asterisk">*</span></label>
                    <input type="number" value={gemstonePrice} onChange={ e => setGemstonePrice(e.target.value) } />
                </div>

                <div className="form-group">
                    <label>Status<span className="required-asterisk">*</span></label>
                    <select value={status} onChange={ e => setStatus(e.target.value) }>
                        {statuses.map(s => (
                        <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Image File<span className="required-asterisk">*</span></label>
                    <input
                        id="file-upload"
                        type="file"
                        accept=".png,.jpg,.svg,.jpeg,.gif"
                        onChange={handleFileChange}
                    />

                    {files.length === 0 ? (
                        <label htmlFor="file-upload" className="custom-file-upload">
                            Choose File
                        </label>
                    ) : (
                        <div className="uploaded-file-item">
                            {files[0].name}
                            <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => removeFile(0)}
                                aria-label={`Remove ${files[0].name}`}
                            >
                                ×
                            </button>
                        </div>
                    )}
                </div>

                <div className="modal-actions">
                    <button disabled={ uploading } onClick={onClose}>Cancel</button>
                    <button disabled={ uploading } onClick={handleSubmit}>
                        {
                            !uploading &&
                            <span>{ initialData ? 'Update' : 'Add' }</span>
                        }
                        
                        { uploading && 
                            <svg width="15" height="15" viewBox="0 0 50 50" className="spinner">
                                <circle cx="25" cy="25" r="20" stroke="white" strokeWidth="5" fill="none" strokeDasharray="90 150" strokeLinecap="round">
                                    <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                </circle>
                            </svg>
                        }
                    </button>
                </div>
            </div>
        </div>
    )
}

export default GemstoneEditorModal;